import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PayrollSettingsCard
 * -------------------
 * Small settings card for the org-level HMRC identifiers that
 * flow through to P60 / P45 / Remittance Advice PDFs.
 *
 * Backed by:
 *   GET /api/organizations/me/payroll-settings
 *   PUT /api/organizations/me/payroll-settings
 *
 * Fields (all optional strings; empty clears):
 *   paye_reference       — Employer PAYE reference (e.g. "123/AB456")
 *   paye_office_number   — 3-digit tax office number
 *   accounts_office_ref  — Accounts Office reference
 */
export function PayrollSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    paye_reference: '',
    paye_office_number: '',
    accounts_office_ref: '',
  });
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    api.getOrgPayrollSettings()
      .then((data) => {
        if (!alive) return;
        setForm({
          paye_reference: data.paye_reference || '',
          paye_office_number: data.paye_office_number || '',
          accounts_office_ref: data.accounts_office_ref || '',
        });
        setUpdatedAt(data.payroll_updated_at || null);
      })
      .catch((err) => toast.error('Failed to load payroll settings: ' + err.message))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = await api.updateOrgPayrollSettings(form);
      setUpdatedAt(data.payroll_updated_at || null);
      toast.success('Payroll settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="payroll-settings-card">
        <div style={{ color: '#6b7280' }}>Loading payroll settings…</div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="payroll-settings-card">
      <h3 style={{ margin: 0 }}>🏛️ HMRC / Payroll references</h3>
      <p style={{ marginTop: '0.35rem', fontSize: '0.82rem', color: '#6b7280' }}>
        These identifiers appear on every P60, P45 and Contractor Remittance Advice PDF generated
        by the app. Leave any field blank if you don't have it yet.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginTop: '0.75rem' }}>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Employer PAYE reference</label>
          <input
            type="text"
            value={form.paye_reference}
            onChange={(e) => setForm((f) => ({ ...f, paye_reference: e.target.value }))}
            placeholder="e.g. 123/AB456"
            maxLength={60}
            style={inputStyle}
            data-testid="payroll-paye-ref"
          />
          <div style={hintStyle}>Format usually "3-digit tax office / employer scheme"</div>
        </div>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Tax office number</label>
          <input
            type="text"
            value={form.paye_office_number}
            onChange={(e) => setForm((f) => ({ ...f, paye_office_number: e.target.value }))}
            placeholder="e.g. 123"
            maxLength={10}
            style={inputStyle}
            data-testid="payroll-paye-office"
          />
          <div style={hintStyle}>The 3-digit code before the "/" in your PAYE ref</div>
        </div>
        <div>
          <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Accounts Office reference</label>
          <input
            type="text"
            value={form.accounts_office_ref}
            onChange={(e) => setForm((f) => ({ ...f, accounts_office_ref: e.target.value }))}
            placeholder="e.g. 123PA00012345"
            maxLength={20}
            style={inputStyle}
            data-testid="payroll-accounts-office"
          />
          <div style={hintStyle}>13 characters — from HMRC yellow payslip booklet</div>
        </div>
      </div>
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={save}
          data-testid="payroll-settings-save"
        >
          {saving ? 'Saving…' : '💾 Save'}
        </button>
        {updatedAt && (
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            Last updated {new Date(updatedAt).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.55rem',
  borderRadius: 6,
  border: '1px solid hsl(var(--border))',
  fontSize: '0.9rem',
  marginTop: 4,
};
const hintStyle = { fontSize: '0.72rem', color: '#6b7280', marginTop: 3 };
